import { Box, Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { StepButton, StepTypography } from 'shell/components/shared/step-button';
import { PmiButton } from 'shell/components/shared/button';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import { CancellationAireFormVM } from 'cancellations/view-models/cancellation-aire';
import { transformerCancellationAireData } from 'cancellations/utils/transform-anpr-cancellation-aire';
import CancellationAire from 'cancellations/components/cancellation-aire/content';
import { validationSchema } from 'cancellations/constants/cancellation-aire-validation-schema';
import { CancellationAirePresumedUnavailabiltyCrumbs } from 'cancellations/constants/cancellation-aire-presumed-unavailabilty-crumbs';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';
import PmiTitle from 'shell/components/shared/title';

const CancellationAirePresumedUnavailabiltyPage = (): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';
  const idSchedaSoggettoANPR = queryParams.get('idSchedaSoggettoANPR') || '';

  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CancellationAireFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
            idSchedaSoggettoANPR: idSchedaSoggettoANPR || undefined,
            soggettoAIRE: 'S',
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
            dataRiferimentoRichiesta:
              filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
              undefined,
            comuneResidenza:
              filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
              filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
              filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
              filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
                ? {
                    siglaProvinciaIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.province ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.province ||
                      undefined,
                    codiceIstat:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                      undefined,
                    nomeComune:
                      filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                        ?.description ||
                      filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                        ?.description ||
                      undefined,
                  }
                : undefined,
          },
        },
      },
      transformer: (response) => transformerCancellationAireData(response),
    }
  );

  const [responseCancellationPresumedUnavailability, cancellationPresumedUnavailability] =
    useHttpFetch<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>(
      `${__PERSON_API_URL__}/persona/cancellazione-aire/irreper-presunta`,
      {
        options: {
          method: 'POST',
          errorCatching: true,
          loading: true,
        },
        transformer: transformResponseSaveDataAnpr,
      }
    );

  const castedSavedData = responseCancellationPresumedUnavailability.data as SaveDataAnpr;

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const handleSubmit = useCallback(
    (values: CancellationAireFormVM) => {
      const data = {
        datiSoggetto: values.data,
        dataDecorrenzaCancellazione: values?.cancellationAire?.cancellationDate || undefined,
        iniziativaCancellazione: values?.cancellationAire?.typeInitiative || '',
        allegati:
          values?.document?.files && values?.document?.files?.length > 0
            ? values?.document?.files?.map((file) => ({
                contenutoAllegato: file.raw?.split(',')[1] || '',
                nomeFile: file.name || '',
              }))
            : [],
      };
      (
        cancellationPresumedUnavailability({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;
        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate('/cancellazioni');
      });
    },
    [navigate, cancellationPresumedUnavailability, setVisibility]
  );

  const handleExit = useCallback(() => {
    navigate('/cancellazioni');
  }, [navigate]);

  //Errors;
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  return (
    <Fragment>
      <Breadcrumbs items={CancellationAirePresumedUnavailabiltyCrumbs} />
      <PmiTitle title="Cancellazione AIRE per Irreperibilità Presunta" />
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      {anpr.data && !anpr.isLoading && !anpr.hasErrors ? (
        <Formik
          initialValues={
            {
              ...((anpr.data as CancellationAireFormVM) || {}),
              cancellationAire: {
                cancellationDate: null,
                isCitizenshipLoss: false,
                typeInitiative: '',
              },
            } as CancellationAireFormVM
          }
          onSubmit={handleSubmit}
          validateOnMount
          validationSchema={validationSchema}
        >
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2}>
                <StepButton active fullWidth>
                  <StepTypography active>Anagrafe</StepTypography>
                </StepButton>
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrandThird"
                      variant="outlined"
                      id="submit"
                      fullWidth
                      type="submit"
                    >
                      Conferma
                    </PmiButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      color="cdmBrand"
                      variant="contained"
                      id="deny-operation"
                      fullWidth
                      onClick={handleExit}
                    >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Annulla Operazione
                      </Box>
                    </PmiButton>
                  </div>
                </div>
              </Grid>
              <Grid item xs={12} md={10}>
                <CancellationAire isAire isCitizenship />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      ) : undefined}
    </Fragment>
  );
};

CancellationAirePresumedUnavailabiltyPage.displayName =
  'Cancellation Aire Presumed Unavailability page component';

export default CancellationAirePresumedUnavailabiltyPage;
